import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    FormGroup,
    FormControlLabel,
    Radio,
    RadioGroup,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ProgrammeAPI } from '../utilities/ProgrammeAPI';

/**
 * Dynamic Programme Selector Component
 * Renders programme selection based on field configuration
 */
export function ProgrammeSelector({ 
    field, 
    value = [], 
    onChange, 
    error = null 
}) {
    const [programmes, setProgrammes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState(null);
    const [selectedProgrammes, setSelectedProgrammes] = useState(Array.isArray(value) ? value : []);

    // Load programmes on component mount
    useEffect(() => {
        loadProgrammes();
    }, [field.programmeLevel, field.facultyFilter]);

    const loadProgrammes = async () => {
        try {
            setLoading(true);
            setApiError(null);
            
            const response = await ProgrammeAPI.getActiveStructuredProgrammes();
            console.log('Raw API response data:', response.data);
            
            // Flatten the structured response into a single list for easier processing
            let allProgrammes = [];
            if (response.data) {
                for (const facultyKey in response.data) {
                    const facultyObj = response.data[facultyKey];
                    for (const departmentKey in facultyObj) {
                        const departmentObj = facultyObj[departmentKey];
                        for (const levelKey in departmentObj) {
                            const levelArray = departmentObj[levelKey];
                            allProgrammes = allProgrammes.concat(levelArray);
                        }
                    }
                }
            }
            console.log('All flattened programmes:', allProgrammes);            // Apply filters based on field configuration
            let filteredProgrammes = allProgrammes.filter(programme => {
                // Use the faculty and level values provided directly by the backend
                const matchesLevel = !field.programmeLevel || field.programmeLevel === 'all' || 
                                     (programme.level && programme.level.toLowerCase() === field.programmeLevel.toLowerCase());
                const matchesFaculty = !field.facultyFilter || field.facultyFilter === 'all' || 
                                      (programme.faculty && programme.faculty.toLowerCase() === field.facultyFilter.toLowerCase());

                console.log(`Filtering programme ${programme.name}: Level filter='${field.programmeLevel}', Faculty filter='${field.facultyFilter}'`);
                console.log(`  Programme faculty: ${programme.faculty}, level: ${programme.level}`);
                console.log(`  Matches Level: ${matchesLevel}, Matches Faculty: ${matchesFaculty}`);

                return matchesLevel && matchesFaculty;
            });

            console.log('Filtered programmes:', filteredProgrammes);
            setProgrammes(filteredProgrammes);
        } catch (err) {
            console.error('Error loading programmes:', err);
            setApiError(`Failed to load programmes: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };    const handleSelection = (programmeId, isSelected) => {
        let newSelection = [...selectedProgrammes];
        const maxSelections = field.maxSelections || 1; // Default to 1 for single selection

        if (maxSelections === 1) {
            // Single selection mode
            newSelection = isSelected ? [programmeId] : [];
        } else {
            // Multiple selection mode
            if (isSelected) {
                if (!newSelection.includes(programmeId)) { // Add only if not already selected
                    if (newSelection.length < maxSelections) {
                        newSelection.push(programmeId);
                    } else {
                        // If max selections reached, replace the oldest selection or do nothing
                        // For simplicity, let's just prevent adding more if max is reached
                        // A more complex UI might allow deselecting one to select another
                    }
                }
            } else {
                newSelection = newSelection.filter(id => id !== programmeId);
            }
        }

        setSelectedProgrammes(newSelection);
        onChange(newSelection);
    };

    const handleRadioChange = (programmeId) => {
        // For single selection, directly set the new selection
        const newSelection = [programmeId];
        setSelectedProgrammes(newSelection);
        onChange(newSelection);
    };

    const isSelected = (programmeId) => {
        return selectedProgrammes.includes(programmeId);
    };

    const canSelectMore = () => {
        const maxSelections = field.maxSelections || 1;
        return selectedProgrammes.length < maxSelections;
    };    const renderProgrammesByFaculty = () => {
        const groupedProgrammes = {};
        
        programmes.forEach(programme => {
            // Use the faculty, department, and level values provided directly by the backend
            const faculty = programme.faculty || 'Other';
            const department = programme.department || 'General';
            const level = programme.level || 'Unknown Level';

            if (!groupedProgrammes[faculty]) {
                groupedProgrammes[faculty] = {};
            }
            if (!groupedProgrammes[faculty][department]) {
                groupedProgrammes[faculty][department] = {};
            }
            if (!groupedProgrammes[faculty][department][level]) {
                groupedProgrammes[faculty][department][level] = [];
            }
            
            groupedProgrammes[faculty][department][level].push(programme);
        });        return Object.entries(groupedProgrammes).map(([faculty, departments]) => (
            <Accordion key={faculty} defaultExpanded={Object.keys(groupedProgrammes).length === 1}>
                <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    onClick={(e) => {
                        // Only prevent if the click target is a form control
                        if (e.target.closest('.MuiFormControlLabel-root, .MuiCheckbox-root, .MuiRadio-root')) {
                            e.stopPropagation();
                        }
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                        {faculty}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {Object.entries(departments).map(([department, levels]) => (
                        <Box key={department} sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                                {department}
                            </Typography>
                            {Object.entries(levels).map(([level, levelProgrammes]) => (                                    <Box key={level} sx={{ ml: 2, mb: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                                            {level}
                                        </Typography>
                                        <Box sx={{ ml: 2 }}>
                                            {levelProgrammes.map(programme => renderProgrammeItem(programme))}
                                        </Box>
                                    </Box>
                            ))}
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>
        ));
    };    const renderProgrammeItem = (programme) => {
        const isChecked = isSelected(programme.id);
        const maxSelections = field.maxSelections || 1;
        const disabled = (maxSelections > 1 && !canSelectMore() && !isChecked);

        return (
            <FormControlLabel
                key={programme.id}
                value={programme.id} // This is crucial for RadioGroup to work
                control={
                    maxSelections === 1 ? (
                        <Radio disabled={disabled} />
                    ) : (
                        <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                                e.stopPropagation(); // Prevent accordion toggle
                                handleSelection(programme.id, e.target.checked);
                            }}
                            disabled={disabled}
                        />
                    )
                }
                label={
                    <Box>
                        <Typography variant="body2">{programme.name}</Typography>
                        {programme.description && (
                            <Typography variant="caption" color="text.secondary">
                                {programme.description}
                            </Typography>
                        )}
                    </Box>
                }
                onClick={(e) => {
                    // For single selection (radio), let RadioGroup handle it
                    // For multiple selection (checkbox), we handle it manually
                    if (maxSelections > 1) {
                        e.stopPropagation(); // Prevent accordion toggle
                    }
                }}
            />
        );
    };const renderFlatList = () => {
        return (
            <Box>
                {programmes.map(programme => renderProgrammeItem(programme))}
            </Box>
        );
    };

    const renderHierarchical = () => {
        if (field.displayStyle === 'flat') {
            return renderFlatList();
        } else {
            return renderProgrammesByFaculty();
        }
    };

    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading programmes...
                </Typography>
            </Box>
        );
    }

    if (apiError) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {apiError}
            </Alert>
        );
    }

    if (programmes.length === 0) {
        return (
            <Alert severity="info" sx={{ mb: 2 }}>
                No programmes available for the selected criteria.
            </Alert>
        );
    }

    const maxSelections = field.maxSelections || 1;

    return (
        <Box>
            {/* Selection Summary */}
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {maxSelections === 1 
                        ? 'Select one programme:' 
                        : `Select up to ${maxSelections} programmes:`
                    }
                </Typography>
                
                {selectedProgrammes.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                            Selected ({selectedProgrammes.length}):
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                            {selectedProgrammes.map(progId => {
                                const programme = programmes.find(p => p.id === progId);
                                return programme ? (
                                    <Chip
                                        key={progId}
                                        label={programme.name}
                                        size="small"
                                        onDelete={() => handleSelection(progId, false)}
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                    />
                                ) : null;
                            })}
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}            {/* Programme Selection */}
            {maxSelections === 1 ? (
                <RadioGroup
                    value={selectedProgrammes[0] || ''}
                    onChange={(e) => {
                        handleRadioChange(e.target.value);
                    }}
                >
                    {renderHierarchical()}
                </RadioGroup>
            ) : (
                <Box>
                    {renderHierarchical()}
                </Box>
            )}

            {/* Selection Limit Warning */}
            {maxSelections > 1 && !canSelectMore() && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    You have reached the maximum selection limit of {maxSelections} programmes.
                </Alert>
            )}
        </Box>
    );
}

export default ProgrammeSelector;
